import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import axios from "../../../api/axios";
import { fetchJournalDay, saveJournalDay, updateTodoDigestPreferences } from "../../../api/workJournal";
import { TodoTaskList } from "./TodoTaskList";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { WorkJournalRichEditor } from "../../../components/dashboard/todos/WorkJournalRichEditor";
import {
  Button,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slide,
  Tabs,
  Tab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  mergeTodoWorkspaceParams,
  parseWorkspaceViewTab,
} from "./todoWorkspaceSearchParams";

const DialogTransition = React.forwardRef(function DialogTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function toYmd(d) {
  const x = new Date(d);
  const p = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
}

function WorkspaceJournalAside({
  journalDate,
  setJournalDate,
  shiftDay,
  journalHtml,
  journalLoading,
  journalFullscreen,
  digestMorning,
  setDigestMorning,
  digestEvening,
  setDigestEvening,
  digestTz,
  setDigestTz,
  onJournalChange,
  onOpenFullscreen,
  persistDigest,
  editorPlaceholder,
  t,
}) {
  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0 p-3 sm:p-4 gap-3 overflow-visible">
      <JournalChrome
        journalDate={journalDate}
        setJournalDate={setJournalDate}
        shiftDay={shiftDay}
        onOpenFullscreen={onOpenFullscreen}
        disableFullscreen={journalLoading}
        t={t}
      />

      <div className="flex-1 min-h-[220px] lg:min-h-0 min-w-0 flex flex-col overflow-visible">
        {journalLoading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm rounded-xl border border-dashed border-gray-200 bg-white/60">
            {t("todo.aiLoading")}
          </div>
        ) : journalFullscreen ? (
          <div className="flex-1 flex items-center justify-center rounded-xl border border-dashed border-blue-200 bg-white/80 text-sm text-gray-600 text-center px-4 py-10">
            {t("todo.journalOpenInFullscreen")}
          </div>
        ) : (
          <WorkJournalRichEditor
            value={journalHtml}
            onChange={onJournalChange}
            placeholder={editorPlaceholder}
          />
        )}
      </div>

      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        className="!rounded-lg !border !border-gray-200 !bg-white/90 before:hidden shrink-0"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="min-h-[44px] px-3">
          <Typography variant="subtitle2" className="text-gray-700">
            {t("todo.digestTitle")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="pt-0 px-3 pb-3">
          <FormControlLabel
            control={
              <Switch
                checked={digestMorning}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDigestMorning(v);
                  persistDigest({ todo_morning_digest_enabled: v });
                }}
              />
            }
            label={<span className="text-sm">{t("todo.digestMorning")}</span>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={digestEvening}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDigestEvening(v);
                  persistDigest({ todo_evening_digest_enabled: v });
                }}
              />
            }
            label={<span className="text-sm">{t("todo.digestEvening")}</span>}
          />
          <TextField
            fullWidth
            size="small"
            label={t("todo.digestTimezone")}
            value={digestTz}
            onChange={(e) => setDigestTz(e.target.value)}
            onBlur={() => persistDigest({ todo_digest_timezone: digestTz })}
            className="mt-2"
            helperText="e.g. Europe/Oslo"
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

function JournalChrome({ journalDate, setJournalDate, shiftDay, onOpenFullscreen, disableFullscreen, t }) {
  return (
    <div className="flex flex-col gap-2 shrink-0 w-full min-w-0 max-w-full">
      <div className="flex items-center gap-2 w-full min-w-0 justify-between">
        <Typography
          variant="subtitle2"
          component="span"
          className="font-semibold text-gray-900 truncate min-w-0 pr-2"
          sx={{ fontSize: "0.8125rem" }}
        >
          {t("todo.journalTitle")}
        </Typography>
        <div className="flex items-center gap-0.5 shrink-0">
          <IconButton size="small" aria-label={t("todo.prevDay")} onClick={() => shiftDay(-1)}>
            <ChevronLeftIcon className="h-5 w-5" />
          </IconButton>
          <Button size="small" variant="outlined" onClick={() => setJournalDate(toYmd(new Date()))}>
            {t("todo.today")}
          </Button>
          <IconButton size="small" aria-label={t("todo.nextDay")} onClick={() => shiftDay(1)}>
            <ChevronRightIcon className="h-5 w-5" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            aria-label={t("todo.journalFullscreen")}
            onClick={onOpenFullscreen}
            title={t("todo.journalFullscreen")}
            disabled={disableFullscreen}
          >
            <ArrowsPointingOutIcon className="h-5 w-5" />
          </IconButton>
        </div>
      </div>
      <TextField
        type="date"
        size="small"
        fullWidth
        label={t("todo.journalDate")}
        value={journalDate}
        onChange={(e) => setJournalDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <p className="text-xs text-gray-500 leading-snug">{t("todo.journalHintRich")}</p>
    </div>
  );
}

export const TodoWorkspace = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const mobileWorkspaceTab = parseWorkspaceViewTab(searchParams);

  const setWorkspaceTab = useCallback(
    (tabIndex) => {
      setSearchParams(
        (prev) =>
          mergeTodoWorkspaceParams(prev, {
            view: tabIndex === 1 ? "journal" : null,
          }),
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [journalDate, setJournalDate] = useState(() => toYmd(new Date()));
  const [journalHtml, setJournalHtml] = useState("");
  const [journalLoading, setJournalLoading] = useState(true);
  const [journalFullscreen, setJournalFullscreen] = useState(false);
  const saveTimer = useRef(null);

  const [digestMorning, setDigestMorning] = useState(false);
  const [digestEvening, setDigestEvening] = useState(false);
  const [digestTz, setDigestTz] = useState("Europe/Oslo");

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get("/users/me/settings");
      const s = res?.data?.settings || {};
      setDigestMorning(!!s.todo_morning_digest_enabled);
      setDigestEvening(!!s.todo_evening_digest_enabled);
      setDigestTz(s.todo_digest_timezone || "Europe/Oslo");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    let cancelled = false;
    setJournalLoading(true);
    fetchJournalDay(journalDate)
      .then((row) => {
        if (!cancelled) {
          setJournalHtml(row?.body_html || "");
        }
      })
      .catch(() => {
        if (!cancelled) setJournalHtml("");
      })
      .finally(() => {
        if (!cancelled) setJournalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [journalDate]);

  const scheduleSave = useCallback(
    (html) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          await saveJournalDay(journalDate, html);
        } catch {
          toast.error(t("todo.journalSaveError"));
        }
      }, 900);
    },
    [journalDate, t]
  );

  const onJournalChange = useCallback(
    (v) => {
      setJournalHtml(v);
      scheduleSave(v);
    },
    [scheduleSave]
  );

  const shiftDay = (delta) => {
    const d = new Date(journalDate + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setJournalDate(toYmd(d));
  };

  const persistDigest = async (patch) => {
    try {
      await updateTodoDigestPreferences(patch);
      toast.success(t("todo.digestSaved"));
    } catch {
      toast.error(t("todo.digestSaveError"));
    }
  };

  const editorPlaceholder = t("todo.journalPlaceholder");

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50">
      <DashboardHeader title={t("todo.pageTitle")} />

      <div className="shrink-0 border-b border-gray-200 bg-white lg:hidden">
        <Tabs
          value={mobileWorkspaceTab}
          onChange={(_, v) => setWorkspaceTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 44 }}
        >
          <Tab label={t("todo.tabTasks")} sx={{ minHeight: 44, textTransform: "none" }} />
          <Tab label={t("todo.journalTitle")} sx={{ minHeight: 44, textTransform: "none" }} />
        </Tabs>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0 min-w-0">
        <div
          className={
            mobileWorkspaceTab !== 0
              ? "hidden lg:flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden"
              : "flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden"
          }
        >
          <TodoTaskList hideHeader />
        </div>

        <aside
          className={
            mobileWorkspaceTab !== 1
              ? "hidden lg:flex w-full lg:flex-none lg:w-[clamp(380px,42vw,620px)] shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 bg-[#fbfbfa] flex-col min-h-0 min-w-0 lg:max-h-none relative z-[5] overflow-visible"
              : "flex w-full lg:flex-none lg:w-[clamp(380px,42vw,620px)] shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 bg-[#fbfbfa] flex-1 lg:flex-initial flex-col min-h-0 min-w-0 lg:max-h-none relative z-[5] overflow-visible"
          }
        >
          <WorkspaceJournalAside
            journalDate={journalDate}
            setJournalDate={setJournalDate}
            shiftDay={shiftDay}
            journalHtml={journalHtml}
            journalLoading={journalLoading}
            journalFullscreen={journalFullscreen}
            digestMorning={digestMorning}
            setDigestMorning={setDigestMorning}
            digestEvening={digestEvening}
            setDigestEvening={setDigestEvening}
            digestTz={digestTz}
            setDigestTz={setDigestTz}
            onJournalChange={onJournalChange}
            onOpenFullscreen={() => setJournalFullscreen(true)}
            persistDigest={persistDigest}
            editorPlaceholder={editorPlaceholder}
            t={t}
          />
        </aside>
      </div>

      <Dialog
        fullScreen
        open={journalFullscreen}
        onClose={() => setJournalFullscreen(false)}
        TransitionComponent={DialogTransition}
        PaperProps={{ className: "bg-[#fafaf9]" }}
      >
        <AppBar position="sticky" elevation={0} color="inherit" className="border-b border-gray-200 bg-white/95 backdrop-blur-sm">
          <Toolbar variant="dense" className="gap-2 flex-wrap min-h-[56px] h-auto py-2">
            <Typography variant="subtitle1" className="flex-1 min-w-[120px] font-semibold">
              {t("todo.journalTitle")}
            </Typography>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <IconButton size="small" aria-label={t("todo.prevDay")} onClick={() => shiftDay(-1)}>
                <ChevronLeftIcon className="h-5 w-5" />
              </IconButton>
              <Button size="small" variant="outlined" onClick={() => setJournalDate(toYmd(new Date()))}>
                {t("todo.today")}
              </Button>
              <IconButton size="small" aria-label={t("todo.nextDay")} onClick={() => shiftDay(1)}>
                <ChevronRightIcon className="h-5 w-5" />
              </IconButton>
              <TextField
                type="date"
                size="small"
                value={journalDate}
                onChange={(e) => setJournalDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", sm: 160 } }}
              />
              <Button size="medium" variant="contained" color="primary" onClick={() => setJournalFullscreen(false)} className="!ml-1">
                {t("todo.journalExitFullscreen")}
              </Button>
            </div>
          </Toolbar>
        </AppBar>
        <div className="flex-1 overflow-auto p-3 sm:p-6 max-w-5xl mx-auto w-full min-h-[calc(100vh-64px)] flex flex-col">
          {journalLoading ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">{t("todo.aiLoading")}</div>
          ) : (
            <WorkJournalRichEditor
              fullscreen
              value={journalHtml}
              onChange={onJournalChange}
              placeholder={editorPlaceholder}
            />
          )}
        </div>
      </Dialog>
    </div>
  );
};
